import type { CSSProperties, ReactNode } from "react";

import styles from "./MainColumn.module.css";

export type MainColumnProps = {
  /** Figma's padding, in design pixels. */
  readonly paddingTop: number;
  readonly paddingInline: number;
  readonly paddingBottom: number;
  readonly children: ReactNode;
};

/**
 * The column of sections, filling the screen top to bottom.
 *
 * Figma has this on SPACE_BETWEEN, so the vertical rhythm is a consequence of
 * the screen's height rather than a set of coordinates: at the full 940 the
 * gaps come out at Figma's 60, and a shorter screen closes them evenly.
 *
 * Figma also gives the frame an `itemSpacing` of 48, which is not reproduced.
 * On a 940-tall frame it is a floor the automatic gap never reaches, but the
 * sections and the padding come to exactly 640 — the shortest screen this
 * design is drawn for — so honouring it as a real minimum would push the last
 * two sections off the bottom of every phone shorter than 880.
 */
export function MainColumn({
  paddingTop,
  paddingInline,
  paddingBottom,
  children,
}: MainColumnProps) {
  const padding = {
    "--pad-top": paddingTop,
    "--pad-inline": paddingInline,
    "--pad-bottom": paddingBottom,
  } as CSSProperties;

  return (
    <div className={styles.column} style={padding} data-main-column="">
      {children}
    </div>
  );
}
